import {
  compact,
  find,
  flow,
  initial,
  negate,
  replace,
  startsWith,
  take,
  uniq,
  without,
} from 'lodash';
import { assocTree, TreeData } from 'components/GenericTree';
import { normalize } from '../utils';
import { ApiItem } from '../types';
import {
  Actions,
  CollapseAction,
  DeleteNodeAction,
  ExpandAction,
  FilterAction,
  RenameAction,
  SetEditAction,
  SetLoadingAction,
  SetTreeAction,
  COLLAPSE,
  DELETE,
  EDIT,
  EXPAND,
  FILTER,
  RENAME,
  RESET_FILTER,
  SET_LOADING,
  SET_TREE,
} from './actions';

const doesNotStartWith = negate(startsWith);

interface State {
  tree: TreeData;
  data: ApiItem[];
  filteredTree?: TreeData;
  loading: boolean;
}

const lowerCaseIncludes = (input: string, candidate: string) =>
  input.toLowerCase().includes(candidate.toLowerCase());

export const pathBuilder = (input: string) => {
  const parts = input.split('/');

  return parts.reduce<string[]>(
    (acc, part, index) => [...acc, take(parts, index + 1).join('/')],
    [],
  );
};

function filterItems(state: State, query: string) {
  const matches = state.data.reduce<string[]>((acc, item) => {
    if (lowerCaseIncludes(item.path, query)) {
      return [...acc, item.path];
    }
    return acc;
  }, []);

  const buildParentPaths = flow([pathBuilder, initial]);

  const parents = matches.reduce<string[]>(
    (acc, match) => [...acc, ...buildParentPaths(match)],
    [],
  );

  const filteredData = compact(
    [...matches, ...parents].map(key => find(state.data, ['path', key])),
  );

  return normalize(uniq(filteredData), true);
}

const handleSetLoading = (state: State, { payload }: SetLoadingAction) => ({
  ...state,
  loading: payload,
});

const handleSetTree = (state: State, { payload }: SetTreeAction) => ({
  ...state,
  tree: payload,
});

const handleExpand = (state: State, { payload }: ExpandAction) => {
  const { data } = state;
  const dataEntry = find(data, ['id', payload]);

  if (dataEntry) {
    const newData = compact([
      ...without(data, dataEntry),
      { ...dataEntry, isExpanded: true },
    ]);

    return {
      ...state,
      data: newData,
      tree: assocTree(payload, { isExpanded: true }, state.tree),
    };
  }

  return state;
};

const handleCollapse = (state: State, { payload }: CollapseAction) => {
  const { data } = state;
  const dataEntry = find(data, ['id', payload]);

  if (dataEntry) {
    const newData = [
      ...without(data, dataEntry),
      { ...dataEntry, isExpanded: false },
    ];

    return {
      ...state,
      data: newData,
      tree: assocTree(payload, { isExpanded: false }, state.tree),
    };
  }

  return state;
};

const handleFilter = (state: State, { payload }: FilterAction) => ({
  ...state,
  filteredTree: filterItems(state, payload),
});

const handleResetFilter = (state: State) => ({
  ...state,
  filteredTree: undefined,
});

const handleEdit = (state: State, { payload }: SetEditAction) => ({
  ...state,
  tree: assocTree(payload.id, { isBeingEdited: payload.value }, state.tree),
});

const handleRename = (state: State, { payload }: RenameAction) => {
  const { data } = state;
  const { id, value } = payload;

  const match = find(data, ['path', id]);

  if (!match) {
    return state;
  }

  const trimmedData = without(data, match);
  const pathParts = id.split('/');
  const newPath =
    pathParts.length > 1 ? `${initial(pathParts).join('/')}/${value}` : value;

  const newEntry = {
    ...match,
    id: newPath,
    name: value,
    path: newPath,
  };

  const updateItemsWithNewPath = () =>
    trimmedData.map(item =>
      startsWith(item.path, match.path)
        ? {
            ...item,
            id: replace(item.path, match.path, newPath),
            path: replace(item.path, match.path, newPath),
          }
        : item,
    );

  const dataWithNewName =
    match.type === 'dir' ? updateItemsWithNewPath() : trimmedData;

  const updatedData = [...dataWithNewName, newEntry];

  return {
    ...state,
    data: updatedData,
    tree: normalize(updatedData),
  };
};

const handleDelete = (state: State, { payload }: DeleteNodeAction) => {
  const match = find(state.data, ['path', payload]);

  if (!match) {
    return state;
  }

  const dataWithoutMatch = without(state.data, match);

  const newData =
    match.type === 'dir'
      ? dataWithoutMatch.filter(item =>
          doesNotStartWith(item.path, `${payload}/`),
        )
      : dataWithoutMatch;

  const newTree = normalize(newData);

  return {
    ...state,
    data: newData,
    tree: newTree,
  };
};

const initialState: State = {
  tree: { rootId: '', items: {} },
  data: [],
  filteredTree: undefined,
  loading: false,
};

// prettier-ignore
function reducer(state: State = initialState, action: Actions): State {
  switch (action.type) {
    case SET_LOADING: return handleSetLoading(state, action);
    case SET_TREE: return handleSetTree(state, action);
    case EXPAND: return handleExpand(state, action);
    case COLLAPSE: return handleCollapse(state, action);
    case FILTER: return handleFilter(state, action);
    case RESET_FILTER: return handleResetFilter(state);
    case EDIT: return handleEdit(state, action);
    case RENAME: return handleRename(state, action);
    case DELETE: return handleDelete(state, action);
    default: return state;
  }
}

export default reducer;
